/**
 * /owner/membership/members — list with filter tabs + assign CTA + detail.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import {
  PageHeader, SectionCard, EmptyState, FullSpinner, QueryError, StatusPill, RangeTabs,
} from '@/components/ui_primitives/Misc';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui_primitives/Table';
import Button from '@/components/ui_primitives/Button';
import Modal from '@/components/ui_primitives/Modal';
import { Input, FieldRow } from '@/components/ui_primitives/Field';
import AssignMembershipModal from '@/components/membership/AssignMembershipModal';
import TierBadge from '@/components/membership/TierBadge';
import { rupiah, formatDate, expiryLabel, STATUS_PILL } from '@/lib/membershipFmt';

const FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'all', label: 'All' },
];

export default function OwnerMembershipMembersPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const [status, setStatus] = useState('active');
  const [assignOpen, setAssignOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const filters = status === 'all' ? {} : { status };
  const listQ = useQuery({
    queryKey: qk.ownerMembershipMembers(brandId, { status }),
    queryFn: () => api.ownerMembershipMembers(token, brandId, filters),
    enabled: !!token && !!brandId,
  });

  if (listQ.isLoading) return <FullSpinner />;
  if (listQ.isError) return <QueryError error={listQ.error} onRetry={() => listQ.refetch()} />;

  const rows = listQ.data?.items ?? [];

  return (
    <div data-testid="owner-membership-members-page">
      <PageHeader
        kicker="Memberships"
        title="Members"
        subtitle="Every customer who currently has — or has previously had — a paid membership with this brand."
        actions={
          <Button
            variant="primary" leftIcon={<Plus size={14} />}
            onClick={() => setAssignOpen(true)}
            testID="members-assign-button"
          >
            Assign Membership
          </Button>
        }
      />

      <div className="mb-4">
        <RangeTabs
          value={status} onChange={setStatus} options={FILTERS}
          testID="members-filter-tabs"
        />
      </div>

      <SectionCard padded={false}>
        {rows.length === 0 ? (
          <div className="p-8">
            <EmptyState
              title="No memberships in this state"
              subtitle="Switch tabs to see members in another lifecycle stage, or assign a new membership."
              testID="members-empty"
            />
          </div>
        ) : (
          <Table testID="members-table">
            <THead>
              <Tr>
                <Th>Customer</Th>
                <Th>Plan / Tier</Th>
                <Th align="center">Status</Th>
                <Th>Started</Th>
                <Th>Expires</Th>
                <Th align="right">Paid</Th>
              </Tr>
            </THead>
            <TBody>
              {rows.map((m) => {
                const sp = STATUS_PILL[m.effective_status] || STATUS_PILL.cancelled;
                return (
                  <Tr key={m.id} onClick={() => setDetailId(m.id)} testID={`members-row-${m.id}`}>
                    <Td>
                      <div className="font-medium">{m.customer?.name || m.customer_id}</div>
                      <div className="text-[11px] text-white/45 mt-0.5">
                        {m.customer?.email || m.customer?.phone || '—'}
                      </div>
                    </Td>
                    <Td>
                      <div className="font-medium text-sm">{m.plan?.name || '—'}</div>
                      <div className="mt-1">
                        {m.tier ? <TierBadge tier={m.tier} /> : null}
                      </div>
                    </Td>
                    <Td align="center">
                      <StatusPill variant={sp.variant} testID={`members-status-${m.id}`}>
                        {sp.label}
                      </StatusPill>
                    </Td>
                    <Td className="text-white/70">{formatDate(m.start_date)}</Td>
                    <Td className="text-white/70">
                      {m.expiry_date ? formatDate(m.expiry_date) : (
                        <span className="text-gold font-medium">Lifetime</span>
                      )}
                    </Td>
                    <Td align="right" className="font-mono text-gold">
                      {rupiah(m.price_paid)}
                    </Td>
                  </Tr>
                );
              })}
            </TBody>
          </Table>
        )}
      </SectionCard>

      <AssignMembershipModal
        open={assignOpen} onClose={() => setAssignOpen(false)}
        brandId={brandId}
      />

      <MemberDetailModal
        membershipId={detailId}
        onClose={() => setDetailId(null)}
        brandId={brandId}
      />
    </div>
  );
}

function MemberDetailModal({ membershipId, onClose, brandId }) {
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();
  const open = !!membershipId;
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');

  const dq = useQuery({
    queryKey: qk.ownerMembershipMember(membershipId),
    queryFn: () => api.ownerMembershipMember(token, membershipId),
    enabled: open,
  });

  const cancelM = useMutation({
    mutationFn: () => api.ownerMembershipCancel(token, membershipId, reason.trim() || 'manual_cancel'),
    onSuccess: () => {
      toast.success('Membership cancelled');
      qc.invalidateQueries({ queryKey: qk.ownerMembershipMembers(brandId) });
      qc.invalidateQueries({ queryKey: qk.ownerMembershipMember(membershipId) });
      qc.invalidateQueries({ queryKey: qk.ownerMembershipDashboard(brandId) });
      setCancelOpen(false);
      onClose();
    },
    onError: (e) => toast.error(e?.detail || 'Could not cancel'),
  });

  const m = dq.data;
  const sp = m ? (STATUS_PILL[m.effective_status] || STATUS_PILL.cancelled) : null;

  return (
    <>
      <Modal
        open={open} onClose={onClose}
        title="Membership Detail"
        subtitle={m ? `${m.customer?.name || m.customer_id} · ${m.plan?.name || '—'}` : '—'}
        size="lg"
        testID="member-detail-modal"
        footer={
          <>
            <Button variant="ghost" onClick={onClose} testID="member-detail-close">Close</Button>
            {m && m.effective_status === 'active' ? (
              <Button variant="danger" onClick={() => setCancelOpen(true)}
                testID="member-detail-cancel">Cancel Membership</Button>
            ) : null}
          </>
        }
      >
        {dq.isLoading ? <FullSpinner /> : null}
        {m ? (
          <div className="space-y-5">
            <div className="flex items-center gap-3 flex-wrap">
              {m.tier ? <TierBadge tier={m.tier} size="lg" /> : null}
              <StatusPill variant={sp.variant}>{sp.label}</StatusPill>
              {m.expiry_date ? null : <StatusPill variant="gold">Lifetime</StatusPill>}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <Detail label="Plan" value={m.plan?.name} />
              <Detail label="Price paid" value={rupiah(m.price_paid)} />
              <Detail label="Started" value={formatDate(m.start_date)} />
              <Detail label="Expires" value={m.expiry_date ? formatDate(m.expiry_date) : 'Never'} />
              <Detail label="Assigned by" value={m.assigned_by_user_id || '—'} />
              <Detail label="Status (stored)" value={m.status} />
            </div>
            {(m.benefits ?? []).length ? (
              <div>
                <div className="text-xs uppercase tracking-wider text-gold/80 font-semibold mb-2">
                  Benefits
                </div>
                <ul className="space-y-1.5">
                  {m.benefits.map((b) => (
                    <li key={b.id} className="text-sm text-white/85">
                      <span className="text-gold mr-2">•</span>{b.name}
                      {b.description ? <span className="text-white/45"> — {b.description}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {m.cancelled_at ? (
              <div className="text-xs text-rose-300/80">
                Cancelled {formatDate(m.cancelled_at)} · reason: <span className="font-mono">{m.cancellation_reason || '—'}</span>
              </div>
            ) : null}
            <div className="text-xs text-white/45">
              {expiryLabel(m)}
            </div>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel Membership"
        subtitle="The membership will be marked as cancelled. Audit logs preserve the original record."
        size="sm"
        testID="member-cancel-modal"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCancelOpen(false)} disabled={cancelM.isPending}>
              Keep active
            </Button>
            <Button
              variant="danger" loading={cancelM.isPending}
              onClick={() => cancelM.mutate()}
              testID="member-cancel-confirm"
            >
              Confirm cancel
            </Button>
          </>
        }
      >
        <FieldRow label="Cancellation reason" hint="Visible only to staff via the audit log">
          <Input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. customer_request"
            data-testid="member-cancel-reason"
          />
        </FieldRow>
      </Modal>
    </>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-white/45 font-mono">{label}</div>
      <div className="text-sm mt-1">{value || '—'}</div>
    </div>
  );
}
